import { Controller } from "@hotwired/stimulus";
import { captureException, captureMessage } from "@sentry/browser";

import {
  BrowserExtension,
  ExtensionError,
  type ExtensionErrorCode,
} from "~/lib/browser-extension";
import logger from "~/lib/logger";

const log = logger();

export default class ExtensionAuthenticationController extends Controller {
  static override values = {
    extensionId: String,
    clientId: String,
    isAuthenticated: Boolean,
  } as const;

  static override targets = ["status"];

  declare readonly clientIdValue: string;
  declare readonly isAuthenticatedValue: boolean;
  declare readonly statusTargets: HTMLElement[];

  private extension!: BrowserExtension;

  override connect(): void {
    this.extension = new BrowserExtension();
    this.authenticateExtension();
  }

  private async authenticateExtension(): Promise<void> {
    if (!this.isAuthenticatedValue) {
      this.showStatus("authenticate");
      return;
    }
    this.showStatus("booting");

    try {
      await this.extension.boot(this.clientIdValue);
      this.showStatus("success");
    } catch (error) {
      log.error("Extension boot error:", error);
      this.showStatusFromError(error);
    }
  }

  private showStatusFromError(error: unknown): void {
    let code: ExtensionErrorCode | null = null;
    if (error instanceof ExtensionError) {
      code = error.code;
    }
    switch (code) {
      case "not-installed":
        captureMessage("Extension not installed", {
          level: "info",
        });
        this.showStatus("not-installed");
        break;
      case "communication-error":
      case "extension-rejected":
        this.showStatus("error");
        captureException(error);
        break;
      default:
        log.error("Unhandled extension error:", error);
        captureException(error);
        this.showStatus("error");
    }
  }

  private showStatus(
    status: "authenticate" | "not-installed" | "booting" | "error" | "success",
  ): void {
    this.hideAllStatuses();

    const statusElement = this.statusTargets.find(
      (el) => el.dataset.status === status,
    );
    if (statusElement) {
      statusElement.classList.remove("hidden");
    }
  }

  private hideAllStatuses(): void {
    this.statusTargets.forEach((element) => {
      element.classList.add("hidden");
    });
  }
}
